/**
 * ImmersiveWorkoutHeader - Floating header with progress and controls
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Theme } from '../../../../Theme/Theme';
import { useTrans } from '../../../../Store/System/LocalizationStore';

const Container = styled.div`
  padding: 16px;
  padding-top: max(16px, env(safe-area-inset-top));
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const IconButton = styled.button<{ active?: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 22px;
  background: ${({ active }) => (active ? `${Theme.Colors.primary}30` : 'rgba(255, 255, 255, 0.15)')};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  & > i {
    font-size: 24px;
    color: ${({ active }) => (active ? Theme.Colors.primary : '#fff')};
  }
`;

const CenterInfo = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const ModeBadge = styled.button<{ isAuto?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 16px;
  border: none;
  background: ${({ isAuto }) => (isAuto ? `${Theme.Colors.primary}30` : 'rgba(255, 255, 255, 0.2)')};
  color: ${({ isAuto }) => (isAuto ? Theme.Colors.primary : '#fff')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  & > i {
    font-size: 12px;
  }
`;

const ProgressRow = styled.div`
  margin-top: 16px;
  padding: 0 8px;
`;

const ProgressBarBackground = styled.div`
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${({ progress }) => progress * 100}%;
  background: ${Theme.Colors.primary};
  border-radius: 2px;
  transition: width 0.5s ease;
`;

export type ImmersiveWorkoutHeaderProps = {
  progress: number;
  isAutoMode: boolean;
  isAudioEnabled?: boolean;
  isFullscreen?: boolean;
  onClose: () => void;
  onOpenSettings: () => void;
  onToggleMode?: () => void;
  onToggleFullscreen?: () => void;
};

export const ImmersiveWorkoutHeader: React.FC<ImmersiveWorkoutHeaderProps> = observer(
  ({ progress, isAutoMode, isAudioEnabled, isFullscreen, onClose, onOpenSettings, onToggleMode, onToggleFullscreen }) => {
    const { t } = useTrans();

    return (
      <Container>
        <TopRow>
          <IconButton onClick={onClose}>
            <i className="mdi mdi-close" />
          </IconButton>

          <CenterInfo>
            <ModeBadge isAuto={isAutoMode} onClick={onToggleMode}>
              <i className={isAutoMode ? 'mdi mdi-timer-outline' : 'mdi mdi-dumbbell'} />
              {isAutoMode
                ? t('workout.mode.auto', { defaultValue: 'Auto' })
                : t('workout.mode.manual', { defaultValue: 'Manual' })}
            </ModeBadge>
          </CenterInfo>

          <IconButton onClick={onToggleFullscreen} style={{ marginRight: 8 }}>
            <i className={isFullscreen ? 'mdi mdi-fullscreen-exit' : 'mdi mdi-fullscreen'} />
          </IconButton>

          <IconButton active={isAudioEnabled} onClick={onOpenSettings}>
            <i className={isAudioEnabled ? 'mdi mdi-volume-high' : 'mdi mdi-volume-mute'} />
          </IconButton>
        </TopRow>

        <ProgressRow>
          <ProgressBarBackground>
            <ProgressBarFill progress={progress} />
          </ProgressBarBackground>
        </ProgressRow>
      </Container>
    );
  },
);
